import React from 'react';
import { motion } from 'framer-motion';

interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  isDarkModeToggle?: boolean;
}

const Toggle: React.FC<ToggleProps> = ({
  checked,
  onChange,
  label,
  size = 'md',
  disabled = false,
  isDarkModeToggle = false,
}) => {
  // Different sizes for dark mode toggle vs regular toggles
  const darkModeSizes = {
    sm: { width: 'w-36', height: 'h-10', thumb: 'w-8 h-8', icon: 'text-2xl', travel: 108, text: 'text-xs' },
    md: { width: 'w-44', height: 'h-12', thumb: 'w-10 h-10', icon: 'text-3xl', travel: 132, text: 'text-sm' },
    lg: { width: 'w-52', height: 'h-14', thumb: 'w-12 h-12', icon: 'text-4xl', travel: 156, text: 'text-base' },
  };

  const regularSizes = {
    sm: { width: 'w-10', height: 'h-6', thumb: 'w-4 h-4', travel: 16, icon: 'text-sm', text: 'text-xs' },
    md: { width: 'w-12', height: 'h-7', thumb: 'w-5 h-5', travel: 20, icon: 'text-base', text: 'text-sm' },
    lg: { width: 'w-14', height: 'h-8', thumb: 'w-6 h-6', travel: 24, icon: 'text-lg', text: 'text-base' },
  };

  const currentSize = isDarkModeToggle ? darkModeSizes[size] : regularSizes[size];

  // Sun and Moon icons for dark mode toggle
  const SunIcon = () => (
    <div className="w-full h-full bg-white rounded-full flex items-center justify-center shadow-xl border-2 border-orange-300">
      <span className={`${currentSize.icon}`}>☀️</span>
    </div>
  );

  const MoonIcon = () => (
    <div className="w-full h-full bg-gray-900 rounded-full flex items-center justify-center shadow-xl border-2 border-orange-400 relative">
      <span className={`${currentSize.icon}`}>🌙</span>
      <span className="absolute -top-1 -right-1 text-yellow-300 text-sm">⭐</span>
    </div>
  );

  return (
    <div className="flex items-center gap-3">
      <motion.button
        type="button"
        onClick={() => !disabled && onChange(!checked)}
        className={`${currentSize.width} ${currentSize.height} rounded-full p-1 transition-colors duration-300 focus:outline-none focus:ring-2 ${
          isDarkModeToggle ? 'focus:ring-orange-400/50' : 'focus:ring-accent-purple/50'
        } relative ${
          isDarkModeToggle
            ? checked
              ? 'bg-gray-800'  // Dark background for night mode
              : 'bg-gradient-to-r from-orange-400 to-yellow-400'  // Orange background for day mode
            : checked
              ? 'bg-gradient-to-r from-accent-purple to-accent-pink'
              : 'bg-dark-600'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        whileTap={{ scale: disabled ? 1 : 0.95 }}
      >
        {isDarkModeToggle && (
          <>
            {/* Background text labels */}
            <div className="absolute inset-0 flex items-center">
              {/* Show NIGHTMODE when in night mode (checked) - positioned on left */}
              {checked && (
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  <span className={`${currentSize.text} font-bold text-white tracking-wider`}>
                    NIGHTMODE
                  </span>
                </div>
              )}
              {/* Show DAYMODE when in day mode (unchecked) - positioned on right */}
              {!checked && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <span className={`${currentSize.text} font-bold text-white tracking-wider`}>
                    DAYMODE
                  </span>
                </div>
              )}
            </div>
          </>
        )}
        
        {isDarkModeToggle ? (
          // For dark mode toggle: icon IS the sliding element with background text
          <motion.div
            className={`${currentSize.thumb} flex items-center justify-center relative z-10`}
            animate={{
              x: checked ? currentSize.travel : 0,
            }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            style={{ 
              filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))'
            }}
          >
            <motion.div
              initial={false}
              animate={{ 
                rotate: checked ? 360 : 0 
              }}
              transition={{ duration: 0.3 }}
            >
              {checked ? <MoonIcon /> : <SunIcon />}
            </motion.div>
          </motion.div>
        ) : (
          // For regular toggles: white thumb (normal size)
          <motion.div
            className={`${currentSize.thumb} bg-white rounded-full shadow-lg flex items-center justify-center relative z-10`}
            animate={{
              x: checked ? currentSize.travel : 0,
            }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
          />
        )}
      </motion.button>
      
      {label && (
        <span className={`text-sm font-medium ${disabled ? 'text-dark-400' : 'text-white'}`}>
          {label}
        </span>
      )}
    </div>
  );
};

export default Toggle;